import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const heroImage1 = `https://readdy.ai/api/search-image?query=Elegant%20minimalist%20white%20running%20sneakers%20on%20warm%20sand%20colored%20paper%20backdrop%2C%20soft%20afternoon%20light%20from%20right%2C%20clean%20editorial%20composition%2C%20warm%20beige%20tones%2C%20premium%20product%20photography%20with%20delicate%20shadows%2C%20lifestyle%20aesthetic&width=800&height=900&seq=hero-unique-a7f3b2&orientation=portrait`;

  const heroImage2 = `https://readdy.ai/api/search-image?query=Oversized%20black%20streetwear%20hoodie%20flat%20lay%20on%20natural%20linen%20fabric%20texture%2C%20warm%20morning%20light%2C%20minimal%20editorial%20style%2C%20earthy%20neutral%20palette%2C%20clean%20fashion%20photography%2C%20soft%20fabric%20details%20visible%2C%20relaxed%20casual%20vibe&width=800&height=900&seq=hero-unique-c4e8d1&orientation=portrait`;

  const heroImage3 = `https://readdy.ai/api/search-image?query=Designer%20canvas%20tote%20bag%20with%20leather%20straps%20on%20warm%20oatmeal%20colored%20backdrop%2C%20soft%20natural%20daylight%2C%20minimal%20editorial%20composition%2C%20warm%20neutral%20background%2C%20premium%20product%20photography%2C%20elegant%20simplicity%2C%20subtle%20shadows&width=800&height=900&seq=hero-unique-e2b9f5&orientation=portrait`;

  const heroImage4 = `https://readdy.ai/api/search-image?query=Luxury%20watch%20and%20minimal%20jewelry%20accessories%20arranged%20on%20warm%20cream%20ceramic%20surface%2C%20soft%20golden%20hour%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20composition%2C%20warm%20neutral%20tones%2C%20refined%20aesthetic&width=800&height=900&seq=hero-unique-d5a1c8&orientation=portrait`;

  return (
    <section className="relative min-h-screen flex flex-col lg:flex-row overflow-hidden">
      {/* Left Content */}
      <div className="w-full lg:w-1/2 bg-background-50 flex items-center justify-center px-6 md:px-12 lg:px-16 py-20 lg:py-0">
        <div className="w-full max-w-lg">
          <h1 className="font-heading">
            <span className="block text-5xl md:text-7xl lg:text-8xl font-bold text-foreground-950 leading-none">
              {t('hero.title_line1')}
            </span>
            <span className="block text-4xl md:text-6xl lg:text-7xl italic font-medium text-foreground-800 leading-tight mt-2">
              {t('hero.title_line2')}
            </span>
          </h1>
          <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed max-w-md">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-semibold rounded-md hover:bg-foreground-800 transition-colors cursor-pointer"
            >
              {t('hero.cta_spreadsheet')}
              <i className="ri-arrow-right-up-line ml-2 w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 border-2 border-foreground-900 text-foreground-900 text-sm font-semibold rounded-md hover:bg-foreground-900 hover:text-background-50 transition-colors cursor-pointer"
            >
              {t('hero.cta_buy')}
            </a>
          </div>
        </div>
      </div>

      {/* Right Visual */}
      <div className="w-full lg:w-1/2 bg-background-100 relative flex items-center justify-center min-h-[500px] lg:min-h-screen overflow-hidden">
        {/* Background product images - 4 unique images */}
        <div className="absolute inset-0 grid grid-cols-2 gap-2 p-4 opacity-40">
          <div className="rounded-lg overflow-hidden">
            <img src={heroImage1} alt="Sneakers Planilha CSSBuy" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="rounded-lg overflow-hidden">
            <img src={heroImage2} alt="Hoodie Planilha CSSBuy" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="rounded-lg overflow-hidden">
            <img src={heroImage3} alt="Bolsa Planilha CSSBuy" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="rounded-lg overflow-hidden">
            <img src={heroImage4} alt="Acessórios Planilha CSSBuy" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
        </div>

        {/* Glassmorphism dropdown panel */}
        <div ref={dropdownRef} className="relative z-10 w-[90%] max-w-md">
          <div className="backdrop-blur-xl bg-white/70 rounded-3xl p-6 border border-white/50">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between px-5 py-4 bg-white/90 rounded-xl text-sm font-semibold text-foreground-800 cursor-pointer hover:bg-white transition-colors border border-background-200/60"
            >
              <span>{t('hero.select_category')}</span>
              <i className={isOpen ? 'ri-arrow-up-s-line w-5 h-5 flex items-center justify-center text-foreground-500' : 'ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500'}></i>
            </button>
            {isOpen && (
              <div className="mt-3 bg-white/95 backdrop-blur-sm rounded-xl border border-background-200/60 overflow-hidden max-h-[320px] overflow-y-auto">
                {categories.map((cat, i) => (
                  <a
                    key={i}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                    onClick={() => setIsOpen(false)}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden lg:block">
        <div className="w-6 h-10 border-2 border-foreground-400 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1.5 h-3 bg-foreground-400 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}